import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

// types
import { FeedType } from '@/types/feed'

// libs
import { useLiveRoomProduct } from '@/hooks/useLiveRoomProduct'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import { enterRoom } from '@/methods/livemallclub/enterLiveRoom'

// components
import DrawerComponent from '@/components/common/Drawer'
import Image from '@/components/common/Image'
import Button from '@/components/common/livemallclub/Button'
import FollowHeart from '@/components/shared/livemallclub/FollowHeart'
import KolAttendanceList from './KolAttendanceList'
import ProductList from './ProductList'

/// constants
import { ROUTE_PATH } from '@/constants/routePath'
import VideoStatusTag from '../VideoStatusTag'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'

// Styles
import styles from './style.module.scss'
import { execute } from '@tutorabc/webview-jsbridge'
import { BRIDGE_HANDLER } from '@/constants/bridgeHandler'
import { BRIDGE_SHARE_TYPE } from '@/types/livemallclub/share'

const cx = classnames.bind(styles)

type LivePreviewProps = {
  isDrawerOpen: boolean
  onClose: () => void
  handleHeartClick: () => void
  isActive: boolean
} & FeedType['meta']
const LivePreviewDrawer = ({
  isDrawerOpen = false,
  onClose = () => {},
  roomDesc,
  ownerName,
  ownerMemberInfoId,
  roomId,
  ownerAvatarUrl,
  coverPhotoUrl,
  isActive,
  handleHeartClick,
  title,
  startTime,
  liveType,
}: LivePreviewProps) => {
  const { t } = useTranslation()
  const { productList } = useLiveRoomProduct({ roomId, enabled: isDrawerOpen })
  const hasRoomProduct = productList?.length > 0
  const router = useRouter()
  const isPast = liveType === ROOM_LIVE_TYPE.PAST
  const isFuture = liveType === ROOM_LIVE_TYPE.FUTURE

  const handleMoreClick = () => {
    handleRoutePushWithQuery({
      router,
      pathname: `${ROUTE_PATH.PROFILE}/${ownerMemberInfoId}`,
    })
  }

  const handleShare = async () => {
    execute({
      handlerName: BRIDGE_HANDLER.SHARE_LINK,
      data: {
        type: BRIDGE_SHARE_TYPE.ROOM,
        roomId,
        title,
        name: ownerName,
        startTime,
      },
    })
  }

  return (
    <DrawerComponent direction='bottom' open={isDrawerOpen} onClose={onClose} className={cx('drawer')} lockBackgroundScroll>
      <div className={cx('preview')}>
        <div className={cx('preview__header')}>
          <div className={cx('preview__header-merchant')}>
            <div className={cx('preview__header-merchant__image')}>
              <Image src={coverPhotoUrl} fill alt='owner-avatar' style={{ objectFit: 'cover' }} />
            </div>
            <div className={cx('preview__header-merchant__info')}>
              <div className={cx('preview__header-merchant__info-block')}>
                <VideoStatusTag roomId={roomId} typeMap={{ past: isPast, future: isFuture }} time={startTime} isPreviewDrawer />
                <FollowHeart isActive={isActive} forceChangeOnClick onClick={handleHeartClick} />
              </div>
              <div className={cx('preview__header-merchant__info__owner')}>
                <div className={cx('preview__header-merchant__info__owner-logo')}>
                  <Image src={ownerAvatarUrl} fill alt='owner-avatar' />
                </div>
                <div className={cx('preview__header-merchant__info__owner-name')}>{ownerName}</div>
              </div>
            </div>
          </div>
          <div className={cx('preview__header-merchant__info-title')}>{title}</div>
          <p className={cx('preview__header-description')}>{roomDesc}</p>
          <KolAttendanceList roomId={roomId} />
          <div className={cx('preview__header-buttons-wrapper')}>
            <Button onClick={handleMoreClick} theme='pink'>
              {t('Common_View_Vendors')}
            </Button>
            {!isPast && (
              <Button theme='pink' onClick={handleShare}>
                {t('Common_Share')}
              </Button>
            )}
            {isPast && (
              <Button onClick={() => enterRoom({ roomId, liveType, locale: router.locale })} theme='pink'>
                {t('Common_View_Video')}
              </Button>
            )}
          </div>
        </div>
        {hasRoomProduct && <ProductList productList={productList} />}
      </div>
    </DrawerComponent>
  )
}

export default LivePreviewDrawer
